import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';

export type KnowledgeLevel = 'principiante' | 'intermedio' | 'avanzado';

export interface UserProfile {
  id: string;
  knowledge_level: KnowledgeLevel;
  subscription_status: 'trial' | 'active' | 'cancelled' | 'expired';
  subscription_end_date: string | null;
  trial_expired: boolean;
}

export interface Subscription {
  id: string;
  user_id: string;
  status: 'active' | 'trialing' | 'canceled' | 'incomplete' | 'incomplete_expired' | 'past_due' | 'unpaid';
  current_period_starts_at: string;
  current_period_ends_at: string;
  created_at: string;
}

export const useSubscription = () => {
  const { user } = useAuth();

  const { data: profile, isLoading: isLoadingProfile, error: profileError } = useQuery({
    queryKey: ['profile', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;

      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      if (profileError) throw profileError;
      return profile as UserProfile;
    },
    enabled: !!user?.id
  });

  const { data: subscription, isLoading: isLoadingSubscription } = useQuery({
    queryKey: ['subscription', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;

      const { data: activeSubscription, error: subscriptionError } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('user_id', user.id)
        .eq('status', 'active')
        .single();

      if (subscriptionError && subscriptionError.code !== 'PGRST116') {
        throw subscriptionError;
      }

      return activeSubscription as Subscription | null;
    },
    enabled: !!user?.id
  });

  const isLoading = isLoadingProfile || isLoadingSubscription;
  const error = profileError;

  const hasActiveSubscription = subscription?.status === 'active' && 
    new Date(subscription.current_period_ends_at) > new Date();

  // Los usuarios premium tienen acceso a niveles principiante e intermedio
  const accessibleLevels = hasActiveSubscription 
    ? ['principiante', 'intermedio'] 
    : ['principiante'];

  return {
    profile,
    subscription,
    isLoading,
    error,
    knowledgeLevel: profile?.knowledge_level || 'principiante',
    hasAccess: hasActiveSubscription,
    isTrialing: profile?.subscription_status === 'trial',
    isActive: hasActiveSubscription,
    accessibleLevels
  };
};
